import { useEffect, useMemo, useRef, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PaperPlaneTilt, Sparkle, ArrowClockwise } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import MarkdownDocument from "@/components/MarkdownDocument";
import MemoActions from "@/components/MemoActions";

const SUGGESTED = [
  "Give me a firm-wide strategic pulse and top 3 risks this week.",
  "Which clients are at risk and what's the recommended intervention?",
  "Where should partners focus their time this quarter to maximize revenue?",
  "Rank open opportunities by strategic fit and expected value.",
];

export default function YadidAI({ open, onClose, contextClientId, contextEngagementId }) {
  const [sessionId] = useState(() => localStorage.getItem("yadid_ai_session") || (() => {
    const s = `sess_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem("yadid_ai_session", s);
    return s;
  })());
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [ctxClient, setCtxClient] = useState(contextClientId || "");
  const [ctxEng, setCtxEng] = useState(contextEngagementId || "");
  const [clients, setClients] = useState([]);
  const [engagements, setEngagements] = useState([]);
  const listRef = useRef(null);

  // Narrow engagement dropdown to just the picked client. Firm-wide shows all.
  const scopedEngagements = useMemo(
    () => (ctxClient ? engagements.filter((e) => e.client_id === ctxClient) : engagements),
    [ctxClient, engagements],
  );
  const clientName = useMemo(
    () => clients.find((c) => c.id === ctxClient)?.name || "",
    [ctxClient, clients],
  );

  useEffect(() => {
    if (!open) return;
    Promise.all([
      api.get("/ai/history/" + sessionId).then((r) => setMessages(r.data)),
      api.get("/clients").then((r) => setClients(r.data)),
      api.get("/engagements").then((r) => setEngagements(r.data)),
    ]).catch(() => {});
  }, [open, sessionId]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, busy]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: msg, id: `u_${Date.now()}` }, { role: "assistant", content: "", id: `a_${Date.now()}`, streaming: true }]);

    try {
      // Use fetch for SSE streaming (axios doesn't natively support SSE well)
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/ask`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: sessionId,
          message: msg,
          context_client_id: ctxClient || null,
          context_engagement_id: ctxEng || null,
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const chunk of parts) {
          if (!chunk.startsWith("data: ")) continue;
          const payload = chunk.slice(6);
          try {
            const j = JSON.parse(payload);
            if (j.delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { ...copy[copy.length - 1], content: copy[copy.length - 1].content + j.delta };
                return copy;
              });
            } else if (j.error) {
              toast.error("YadidAi: " + j.error);
            }
          } catch (err) {
            // Malformed SSE chunk — skip this frame but keep the stream open.
            if (process.env.NODE_ENV !== "production") {
              // eslint-disable-next-line no-console
              console.debug("[yadid-ai] SSE parse error", err);
            }
          }
        }
      }
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { ...copy[copy.length - 1], streaming: false };
        return copy;
      });
    } catch (e) {
      toast.error("YadidAi failed to respond");
      setMessages((m) => m.slice(0, -1));
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    const s = `sess_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem("yadid_ai_session", s);
    setMessages([]);
    window.location.reload();
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-xl p-0 border-l border-border bg-[hsl(228,16%,6%)] flex flex-col" data-testid="yadid-ai-panel">
        <SheetHeader className="px-5 py-4 border-b border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkle size={18} weight="fill" className="text-primary" />
              <SheetTitle className="font-display text-base tracking-tight">YadidAi · Strategy Copilot</SheetTitle>
            </div>
            <button onClick={reset} className="text-muted-foreground hover:text-primary text-xs flex items-center gap-1" data-testid="ai-reset">
              <ArrowClockwise size={12} /> new session
            </button>
          </div>
          <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground pt-1">Claude Sonnet 4.5 · reasoning with your firm's data</div>
        </SheetHeader>

        {/* Context selectors — engagement list cascades off the picked client so
            the AI's search corpus is always narrowed correctly. Changing the client
            clears any stale engagement selection that no longer belongs. */}
        <div className="px-5 py-3 border-b border-border grid grid-cols-2 gap-3 bg-[hsl(228,14%,8%)]">
          <div>
            <label className="text-[0.625rem] font-mono uppercase text-muted-foreground tracking-widest">Partner org focus</label>
            <Select
              value={ctxClient || "none"}
              onValueChange={(v) => {
                const next = v === "none" ? "" : v;
                setCtxClient(next);
                // Drop any engagement selection that doesn't belong to the new client scope.
                if (ctxEng) {
                  const stillValid = engagements.some((e) => e.id === ctxEng && (!next || e.client_id === next));
                  if (!stillValid) setCtxEng("");
                }
              }}
            >
              <SelectTrigger className="mt-1 h-8 text-xs rounded-none bg-transparent" data-testid="ai-client-context"><SelectValue placeholder="Firm-wide" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Firm-wide</SelectItem>
                {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[0.625rem] font-mono uppercase text-muted-foreground tracking-widest">Engagement focus</label>
            {ctxClient && scopedEngagements.length === 0 ? (
              <div
                className="mt-1 h-8 px-2 flex items-center text-[0.6875rem] text-muted-foreground border border-border/60 bg-secondary/20"
                data-testid="ai-engagement-empty"
              >
                No engagements on this client
              </div>
            ) : (
              <Select value={ctxEng || "none"} onValueChange={(v) => setCtxEng(v === "none" ? "" : v)}>
                <SelectTrigger className="mt-1 h-8 text-xs rounded-none bg-transparent" data-testid="ai-engagement-context">
                  <SelectValue placeholder={ctxClient ? `All engagements on ${clientName}` : "All engagements"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{ctxClient ? `All engagements on ${clientName}` : "All engagements"}</SelectItem>
                  {scopedEngagements.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>

        {/* Messages */}
        <div ref={listRef} className="flex-1 overflow-auto px-5 py-4 space-y-4">
          {messages.length === 0 && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">Start with a strategic question. YadidAi reasons over your clients, engagements, pipeline, and notes.</p>
              <div className="space-y-2">
                {SUGGESTED.map((s, i) => (
                  <button
                    key={i}
                    data-testid={`ai-suggested-${i}`}
                    onClick={() => send(s)}
                    className="w-full text-left text-xs border border-border bg-secondary/40 px-3 py-2 hover:border-primary/50 hover:bg-secondary transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m) => (
            <div key={m.id || m.created_at} className={`animate-fade-up ${m.role === "user" ? "" : ""}`}>
              <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mb-1">
                {m.role === "user" ? "You" : "YadidAi"}
              </div>
              {m.role === "user" ? (
                <div className="text-sm whitespace-pre-wrap leading-relaxed text-foreground">
                  {m.content}
                </div>
              ) : (
                <div className="border-l-2 border-primary/60 pl-3">
                  <MarkdownDocument>{m.content}</MarkdownDocument>
                  {m.streaming && <span className="ml-1 inline-block w-1.5 h-3.5 bg-primary align-middle animate-pulse" />}
                  {!m.streaming && m.content && m.content.trim().length > 40 && (
                    <MemoActions
                      markdown={m.content}
                      title={(m.content.match(/^#\s+(.+)$/m)?.[1] || "YadidAi memo").trim()}
                    />
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Composer */}
        <div className="border-t border-border p-4 bg-[hsl(228,16%,6%)]">
          <div className="flex gap-2 items-end w-full">
            <Textarea
              data-testid="ai-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Ask YadidAi a strategic question…"
              rows={2}
              className="flex-1 min-w-0 rounded-none resize-none bg-secondary/40 border-border text-sm font-normal"
            />
            <Button
              data-testid="ai-send"
              onClick={() => send()}
              disabled={busy || !input.trim()}
              className="shrink-0 rounded-none h-[62px] w-12 p-0 bg-primary text-primary-foreground hover:bg-primary/90"
            >
              <PaperPlaneTilt size={16} weight="fill" />
            </Button>
          </div>
          <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mt-2">Enter to send · Shift+Enter for newline</div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
